import { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { Button } from '../ui/button';

export const HeroSlider = ({ slides }) => {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isAnimating, setIsAnimating] = useState(false);

  // Guard for empty or null slides
  const slideCount = slides ? slides.length : 0;

  // Reset currentSlide if it becomes out of bounds when slides change
  useEffect(() => {
    if (slideCount > 0 && currentSlide >= slideCount) {
      setCurrentSlide(0);
    }
  }, [slides, slideCount, currentSlide]);

  const goToSlide = useCallback((index) => {
    if (isAnimating || slideCount === 0) return;
    setIsAnimating(true);
    setCurrentSlide(index);
    setTimeout(() => setIsAnimating(false), 500);
  }, [isAnimating, slideCount]);

  const nextSlide = useCallback(() => {
    if (slideCount === 0) return;
    goToSlide((currentSlide + 1) % slideCount);
  }, [currentSlide, slideCount, goToSlide]);

  const prevSlide = useCallback(() => {
    if (slideCount === 0) return;
    goToSlide(currentSlide === 0 ? slideCount - 1 : currentSlide - 1);
  }, [currentSlide, slideCount, goToSlide]);

  useEffect(() => {
    if (slideCount === 0) return;
    const timer = setInterval(nextSlide, 6000);
    return () => clearInterval(timer);
  }, [nextSlide, slideCount]);

  if (slideCount === 0) return null;

  return (
    <div data-testid="hero-slider" className="relative h-[90vh] min-h-[600px] overflow-hidden">
      {/* Slides */}
      {slides.map((slide, index) => (
        <div
          key={slide.id}
          className={`absolute inset-0 transition-opacity duration-700 ${
            index === currentSlide ? 'opacity-100 z-10' : 'opacity-0 z-0'
          }`}
        >
          {/* Background Image */}
          <div className="absolute inset-0">
            <img
              src={slide.image}
              alt={slide.title}
              className="w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-r from-white/95 via-white/70 to-transparent" />
          </div>

          {/* Content */}
          <div className="relative h-full max-w-7xl mx-auto px-6 md:px-12 flex items-center">
            <div className="max-w-2xl">
              <span className="inline-block text-xs font-bold uppercase tracking-[0.2em] text-[#47ace2] mb-4 font-manrope">
                Modern Arch
              </span>
              <h1
                className={`text-4xl sm:text-5xl lg:text-6xl font-light tracking-tight text-slate-900 font-outfit mb-6 transition-all duration-700 ${
                  index === currentSlide
                    ? 'translate-y-0 opacity-100'
                    : 'translate-y-8 opacity-0'
                }`}
              >
                {slide.title}
              </h1>
              <p
                className={`text-lg md:text-xl text-slate-600 font-manrope mb-8 transition-all duration-700 delay-100 ${
                  index === currentSlide
                    ? 'translate-y-0 opacity-100'
                    : 'translate-y-8 opacity-0'
                }`}
              >
                {slide.subtitle}
              </p>
              <div
                className={`flex gap-4 transition-all duration-700 delay-200 ${
                  index === currentSlide
                    ? 'translate-y-0 opacity-100'
                    : 'translate-y-8 opacity-0'
                }`}
              >
                <Link to={slide.link}>
                  <Button
                    data-testid={`hero-cta-${slide.id}`}
                    className="bg-[#47ace2] hover:bg-[#1f7db3] text-white rounded-full px-8 py-6 text-lg font-medium"
                  >
                    {slide.cta}
                  </Button>
                </Link>
                <Link to="/contact">
                  <Button
                    variant="outline"
                    className="border-[#47ace2] text-[#47ace2] hover:bg-[#F0F8FF] rounded-full px-8 py-6 text-lg font-medium"
                  >
                    Get in Touch
                  </Button>
                </Link>
              </div>
            </div>
          </div>
        </div>
      ))}

      {/* Navigation Arrows */}
      <button
        onClick={prevSlide}
        data-testid="hero-prev"
        className="absolute left-6 top-1/2 -translate-y-1/2 z-20 w-12 h-12 rounded-full bg-white/90 shadow-lg flex items-center justify-center text-slate-700 hover:bg-white hover:text-[#47ace2] transition-all"
      >
        <ChevronLeft className="w-6 h-6" />
      </button>
      <button
        onClick={nextSlide}
        data-testid="hero-next"
        className="absolute right-6 top-1/2 -translate-y-1/2 z-20 w-12 h-12 rounded-full bg-white/90 shadow-lg flex items-center justify-center text-slate-700 hover:bg-white hover:text-[#47ace2] transition-all"
      >
        <ChevronRight className="w-6 h-6" />
      </button>

      {/* Dots */}
      <div className="absolute bottom-8 left-1/2 -translate-x-1/2 z-20 flex gap-3">
        {slides.map((_, index) => (
          <button
            key={index}
            onClick={() => goToSlide(index)}
            data-testid={`hero-dot-${index}`}
            className={`h-2 rounded-full transition-all duration-300 ${
              index === currentSlide
                ? 'w-8 bg-[#47ace2]'
                : 'w-2 bg-slate-400 hover:bg-slate-600'
            }`}
          />
        ))}
      </div>
    </div>
  );
};
